import React, { useState } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Dimensions,
  ScrollView,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import { useToast } from "../../../../components/LhToastContainer";
import { apiClient, createApiClient } from "../../../../api/apiClient";
import { RootState } from "../../../../store";

// Define __DEV__ if it doesn't exist (for web environments)
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";

interface PulseCheckTouchPointActionProps {
  onComplete: () => boolean | Promise<boolean>; // allow async validation
  onSaveForLater?: () => void;
  onBackToDashboard?: () => void;
  selectedTouchPoint?: any;
  isFormComplete?: boolean; // <-- add this prop
  pdfExportEnabled?: boolean;
  handleEmployeeNameClick?: () => void;
  scrollRef?: React.RefObject<ScrollView>; // Add scroll ref for scrolling to top
  forceDisabled?: boolean; // <-- add forceDisabled prop
  isViewOnlyMode?: boolean; // <-- add isViewOnlyMode prop
}

const PulseCheckTouchPointAction: React.FC<PulseCheckTouchPointActionProps> = ({
  onComplete,
  onSaveForLater,
  onBackToDashboard,
  isFormComplete = true, // <-- default to true for backward compatibility
  pdfExportEnabled = false,
  handleEmployeeNameClick,
  scrollRef,
  forceDisabled = false, // <-- add forceDisabled prop
  isViewOnlyMode = false, // <-- add isViewOnlyMode prop
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [touchPointStatus, setTouchPointStatus] = useState<
    "new" | "in-progress" | "Completed"
  >("new");
  const { showToast } = useToast();

  // Redux selectors
  const userEmail = useSelector(
    (state: RootState) => state.auth?.user?.username
  );
  const selectedEmployeeId = useSelector(
    (state: RootState) => state.nho?.selectedFilters?.employeeGpid
  );
  const location = useSelector(
    (state: RootState) => state.appData?.selectedLocation?.displayText
  );
  // Centralized NHO error modal visibility - used to prevent navigation when an error overlay is shown
  const isNHOErrorVisible = useSelector((state: RootState) => state.nho?.isErrorModalVisible);

  // Check if we're on a mobile device
  const windowWidth = Dimensions.get("window").width;
  const isMobile = windowWidth < 768;

  // PDF exporting is enabled if pdfExportEnabled is true
  // --- DEBUG LOG ---
  if (__DEV__) {
    console.log("PulseCheckTouchPointAction props:", {
      pdfExportEnabled,
      forceDisabled,
      isViewOnlyMode,
      isFormComplete,
      touchPointStatus,
    });
  }
  // Only enable PDF export if pdfExportEnabled is true, not submitting, and status is Completed
  const isPdfExportDisabled = isSubmitting || (touchPointStatus !== "Completed" && !pdfExportEnabled);

  // Function to handle PDF export via email
  const handlePdfExport = async () => {
    // Validate required parameters
    if (!userEmail) {
      console.error("PDF Export Error: User email not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "User email not found. Please log in again.",
        variant: "error",
        position: "top",
      });
      return;
    }

    if (!selectedEmployeeId) {
      console.error("PDF Export Error: Selected employee ID not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "Employee not selected. Please select an employee.",
        variant: "error",
        position: "top",
      });
      return;
    }

    if (!location) {
      console.error("PDF Export Error: Location not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "Location not selected. Please select a location.",
        variant: "error",
        position: "top",
      });
      return;
    }

    try {
      setIsPdfExporting(true);

      console.log(
        "Exporting Pulse Check PDF for employee:",
        selectedEmployeeId
      );
      console.log("PDF Export params:", {
        emailTo: userEmail,
        inspection_module: "nho_pulse",
        id: selectedEmployeeId,
        location: location,
      });

      // Create API client
      const api = createApiClient();

      // Build URL with query parameters
      const url = `/notifications/nho/email/pdf?emailTo=${encodeURIComponent(
        userEmail
      )}&inspection_module=nho_pulse&id=${encodeURIComponent(
        selectedEmployeeId
      )}&location=${encodeURIComponent(location)}`;

      // Make API call to export PDF
      const response = await apiClient(api, url, "GET");

      console.log("PDF Export API Response:", response);

      // Scroll to top and show success message
      scrollRef?.current?.scrollTo({ y: 0, animated: true });

      showToast({
        header: "PDF Export Successful",
        subtext:
          response?.message ||
          "Pulse Check PDF export email sent successfully!",
        variant: "success",
        position: "top",
      });
    } catch (error: any) {
      console.error("Pulse Check PDF Export Error:", error);

      // Scroll to top for error display
      scrollRef?.current?.scrollTo({ y: 0, animated: true });

      // Handle different error types
      let errorMessage = "Failed to send Pulse Check PDF export email";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid request parameters";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error occurred. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast({
        header: "PDF Export Failed",
        subtext: errorMessage,
        variant: "error",
        position: "top",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  const handleSubmit = async (action: "save" | "complete") => {
    try {
      // Only validate for complete action
      if (action === "complete") {
        // Support sync or async onComplete
        const result = onComplete ? onComplete() : true;
        const isValid = typeof result === 'boolean' ? result : await result;
        if (!isValid) {
          return;
        }
      }

      setIsSubmitting(true);

      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (action === "complete") {
        setTouchPointStatus("Completed");

        // Don't navigate away if a centralized NHO error modal is visible
        if (isNHOErrorVisible) {
          console.warn('Navigation suppressed: NHO error modal is visible');
          // keep submission state; caller can close modal and retry
        } else if (onBackToDashboard) {
          onBackToDashboard();
          // Show success toast after navigation
          showToast({
            header: "Success",
            subtext: "Pulse Check Touchpoint completed successfully!",
            variant: "success",
            position: "top",
          });
        }
      } else {
        // For save action, call onSaveForLater which will handle the submission
        setTouchPointStatus("in-progress");
        if (onSaveForLater) {
          onSaveForLater();
          showToast({
            header: "Success",
            subtext: "Pulse Check saved for later successfully!",
            variant: "success",
            position: "top",
          });
        }
      }
    } catch (error) {
      console.error("Pulse Check Submit error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (__DEV__) {
    console.log(
      "PulseCheckTouchPointAction rendered with touchPointStatus:",
      touchPointStatus
    );
  }

  return (
    <View style={styles.actionRow}>
      <View style={styles.buttonColumn}>
        {/* PDF Export Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            styles.pdfExportButton,
            isPdfExportDisabled ? styles.disabledButton : {},
            Platform.OS === "web" && !isPdfExportDisabled
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={isPdfExportDisabled ? undefined : handlePdfExport}
          disabled={isPdfExportDisabled}
          accessibilityState={{ disabled: isPdfExportDisabled }}
          accessibilityLabel={
            isPdfExportDisabled
              ? touchPointStatus !== "Completed"
                ? "PDF Export Not Available - Complete the touchpoint first"
                : "PDF Export Not Available"
              : isPdfExporting
              ? "Sending Email..."
              : "Export to PDF"
          }
        >
          <View style={styles.buttonContent}>
            {isPdfExporting ? (
              <ActivityIndicator
                size="small"
                color="#FFF"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="picture-as-pdf"
                size={isMobile ? 16 : 20}
                color="white"
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                isPdfExportDisabled && styles.buttonTextDisabled,
                isMobile && styles.buttonTextSmall,
              ]}
            >
              {isPdfExportDisabled
                ? touchPointStatus !== "Completed"
                  ? "Complete to Export PDF"
                  : "PDF Export Not Available"
                : isPdfExporting
                ? "Sending Email..."
                : "Export to PDF"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Complete Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            isSubmitting || touchPointStatus === "Completed" || !isFormComplete || forceDisabled || isViewOnlyMode
              ? styles.disabledButton
              : styles.completeButton,
            Platform.OS === "web" &&
            !(isSubmitting || touchPointStatus === "Completed" || !isFormComplete || forceDisabled || isViewOnlyMode)
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={() => handleSubmit("complete")}
          disabled={
            isSubmitting || touchPointStatus === "Completed" || !isFormComplete || forceDisabled || isViewOnlyMode
          }
          accessibilityState={{
            disabled:
              isSubmitting ||
              touchPointStatus === "Completed" ||
              !isFormComplete || forceDisabled || isViewOnlyMode,
          }}
          accessibilityLabel={
            isSubmitting
              ? "Complete Pulse Check form (disabled during submission)"
              : touchPointStatus === "Completed" || forceDisabled || isViewOnlyMode
              ? "Complete form (disabled for Completed touchpoint)"
              : !isFormComplete
              ? "Complete form (disabled until both signatures are added)"
              : "Complete Pulse Check form"
          }
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color="white"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="check-circle"
                size={isMobile ? 16 : 20}
                color="white"
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                isSubmitting && styles.buttonTextDisabled,
                isMobile && styles.buttonTextSmall,
              ]}
            >
              {isSubmitting ? "Submitting..." : "Complete"}
            </Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// Detect if device is iPad (for web, use width heuristic)
const isIPad =
  (Platform.OS === "ios" && (Dimensions.get("window").width >= 768)) ||
  (Platform.OS === "web" && Dimensions.get("window").width >= 768 && Dimensions.get("window").width <= 1024);

// Detect if device is iPhone (iOS with width < 768)
const isIPhone = Platform.OS === "ios" && Dimensions.get("window").width < 768;

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    flexWrap: "wrap",
    gap: 20,
    alignItems: "center",
    width: Platform.OS === "web" && isIPad ? "40%" : "100%",
  },
  buttonColumn: {
    flex: 2,
    flexDirection: isIPhone ? "column" : "row", // Stack vertically on iPhone, horizontally on web/iPad
    gap: 10,
    minWidth: 0,
    width: isIPhone ? "100%" : Platform.OS === "web" || isIPad ? "40%" : "100%", // Full width on iPhone, auto on web/iPad
    alignItems: isIPhone ? "stretch" : "center", // Stretch buttons on iPhone
  },
  buttonFlex: {
    flex: isIPhone ? 0 : 1, // No flex on iPhone (use width instead)
    width: isIPhone ? "100%" : "40%", // Full width on iPhone, fixed 40% on web/iPad
    minWidth: 0, // Allow buttons to shrink if needed
  },
  customButton: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#ccc",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  dashboardButton: {
    backgroundColor: "#6c757d", // Gray for dashboard
  },
  saveButton: {
    backgroundColor: "#FF9D2F", // Orange for save
  },
  completeButton: {
    backgroundColor: "#008242", // Green for complete
  },
  disabledButton: {
    opacity: 1,
    backgroundColor: "#999",
    borderWidth: 1,
    borderColor: "#ccc",
    shadowOpacity: 0.1,
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  pdfExportButton: {
    backgroundColor: "#D32F2F", // Red for PDF export
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    marginRight: 8,
  },
  buttonText: {
    color: "white",
    fontSize: Platform.OS === "web" ? 16 : 14,
    fontWeight: "600",
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  buttonTextDisabled: {
    color: "#FFFFFF",
  },
});

export default PulseCheckTouchPointAction;
